import EventDispatcher from '../events/EventDispatcher';
import {easeIn as defaultEasing} from './easing/linear';
import Ticker from '../Ticker';

/**
 * Instances registry
 * @type {Set}
 * @ignore
 */
let TWEENS = new Set();

/**
 * New created instances. Flushes in TWEENS after the tick.
 * This is to avoid circular ticks when Tween created inside another Tween callback.
 * @type {Set}
 * @ignore
 */
let NEW_TWEENS = new Set(); //

/**
 * @class
 * @inheritDoc
 * @extends module:events.EventDispatcher
 * @classdesc Класс Анимации
 * @memberOf module:animation
 */
class Tween extends EventDispatcher {

    /**
     * @constructor
     * @param {Object} obj Объект с анимируемым свойством
     * @param {string|number} prop Название анимируемого свойства или число
     * @param {number} start Стартовое значение
     * @param {number} end Конечное значение
     * @param {number} duration Длительность
     * @param {module:animation.module:easing.easingCallback} [easing={@link module:animation.module:easing.linear linear.easeIn}] Функция изменения значения (см. {@link module:animation.module:easing animation.easing})
     * @augments EventDispatcher
     * @inheritDoc
     */
    constructor(obj, prop, start, end, duration, easing = defaultEasing) {
        super();

        if (typeof obj != 'object') obj = null;

        if (obj) {
            if (typeof obj[prop] == 'undefined') throw new Error('Trying to tween undefined property "' + prop + '"');
            if (isNaN(obj[prop])) throw new Error('Tweened value can not be ' + ( typeof obj[prop]));
        }
        else {
            if (isNaN(prop)) throw new Error('Tweened value can not be ' + ( typeof prop));
        }

        if (typeof easing != 'function') easing = defaultEasing;

        /**
         * Объект, содержащий анимируемое свойство
         * @type Object
         * @ignore
         */
        this.obj = obj;

        /**
         * Название анимируемого свойства. Значение свойства должно быть числом.
         * @type String
         * @ignore
         */
        this.prop = prop;

        /**
         * Начальное значение
         * @type Number
         * @ignore
         */
        this.start = start;

        /**
         * Конечное значение
         * @type Number
         * @ignore
         */
        this.end = end;

        /**
         * Длительность анимации в фреймах или милисекундах (зависит от Tween.STEP_TYPE)
         * @type Number
         * @ignore
         */
        this.duration = ~~duration;

        /**
         * Функция анимации
         * @type Function
         * @ignore
         */
        this.callback = easing;

        /**
         * Флаг воспроизведения анимации
         * @type Boolean
         * @ignore
         */
        this.playing = false;

        /**
         * Текущая позиция анимации
         * @ignore
         * @type Boolean
         * @ignore
         */
        this._pos = -1;

        /**
         * @member {boolean} module:animation.Tween#autoRewind Нужно ли автоматически повторять анимацию после ее окончания
         */
        this.autoRewind = false;

        /** @ignore */
        this.destroy = false;

        NEW_TWEENS.add(this);
    }

    destructor() {

        super.destructor();

        TWEENS.delete(this);
        NEW_TWEENS.delete(this);
        this.obj = null;
        this.callback = null;
    }

    /**
     * Начинает воспроизведение
     */
    play() {
        this.playing = true;
        this.tick(0);
        return this;
    }

    /**
     * Приостанавливает воспроизведение
     */
    pause() {
        this.playing = false;
        return this;
    }

    /**
     * Отматывает воспроизведение в начало
     */
    rewind() {
        this._pos = -1;
        return this;
    }

    /**
     * Перепрыгивает сразу на последню позицию
     */
    forward() {
        this._pos = this.duration;
        return this;
    }

    /**
     * Останавливает воспроизведение и перематывает в начало
     */
    stop() {
        this.pause();
        this.rewind();
        return this;
    }

    /**
     * Устанавливает изменяемое значение
     * @param {number} val Новое значение анимируемого параметра
     * @ignore
     */
    updateValue(val) {
        if (this.obj) {
            this.obj[this.prop] = val;
        }
        else {
            this.prop = val;
        }

        /**
         * @event Tween#change
         * @type {module:events.SquireEvent}
         * @property {number} position Позиция между стартом и финишем (от 0 до 1)
         * @property {number} value Текущее значение
         */
        this.emit("change", {
            value: val,
            position: ((val - this.start) / (this.end - this.start))
        });

        return this;
    }

    reverse() {
        [this.start, this.end] = [this.end, this.start];
        this.rewind();
        this.play();
    }

    /**
     * Делает очередной шаг анимации
     * @param {number} delta
     * @ignore
     */
    tick(delta = 0) {
        if (!this.playing) return false;

        this._pos += delta;

        // it's possible to use this behavior to create delay before animation
        if (this._pos < 0) return false;

        if (this._pos > this.duration) {
            if (this.autoRewind) {
                this._pos -= this.duration;
            }
            else {
                return this.finish();
            }
        }

        var val = this.start == this.end
            ? this.start * 1
            : this.callback(this._pos, this.start, this.end - this.start, this.duration);

        this.updateValue(val);

        return false;
    }

    /**
     * Завершает анимацию
     */
    finish() {

        this.stop();
        this.updateValue(this.end);

        /**
         * @event Tween#finish
         * @property {number} position Позиция между стартом и финишем (от 0 до 1)
         * @property {number} value Текущее значение
         * @property {boolean} cancelDestroy Нужно ли отменить уничтожение твина
         */
        let e = {
            position: 1,
            value: this.end,
            cancelDestroy: false
        };
        this.emit("finish", e);

        if(!e.cancelDestroy) this.destroy = true;
    }

    //------------------------------------------------------------------------------------------------------------------
    // СЛУЖЕБНОЕ
    //------------------------------------------------------------------------------------------------------------------
    /**
     * Тик всех твинов
     * @param {Number} delta
     * @param {Iterable} [items] All tweens by default
     * @ignore
     */
    static tick(delta, items = TWEENS) {
        this.cleanup();
        this.flush();
        for (let t of items) {
            t.tick(delta);
        }
    }

    /**
     * Созданные при тике твины сливаются в общий котёл
     * @ignore
     */
    static flush() {
        for (let tween of NEW_TWEENS) {
            TWEENS.add(tween);
        }
        NEW_TWEENS.clear();
    }

    /**
     * Очистка твинов
     * @ignore
     */
    static cleanup() {
        let destroyed = this.filter('destroy', true);
        this.destroy(destroyed);
    }

    /**
     * Уничтожение твинов
     * @param {Iterable} [items] All tweens by default
     * @ignore
     */
    static destroy(items = [...TWEENS, ...NEW_TWEENS]) {
        for (let t of items) {
            t.destructor();
        }
    }

    /**
     * Пометка твинов на уничтожение при ближайшем тике
     * @param {Iterable} [items] All tweens by default
     * @ignore
     */
    static softDestroy(items = [...TWEENS, ...NEW_TWEENS]) {
        for (let t of items) {
            t.destroy = true;
        }
    }

    /**
     * Найти все твины по критерию
     * @param {String} prop список объектов
     * @param {...*} values список значений
     * @return {Tween[]} все твины с таким значением указанного свойства (строгое стравнение)
     * @ignore
     */
    static filter(prop, ...values) {
        let result = [...TWEENS, ...NEW_TWEENS];
        if (undefined !== prop && values.length) {
            for (let val of values) {
                result = result.filter(
                    tween => tween[prop] === val
                );
            }
        }
        return result;
    }

    /**
     * Поиск всех твинов где в качестве объекта выступает любой из переданных аргументов
     * @method module:animation.Tween.findByTarget
     * @param {...*} targets список объектов
     * @return {Tween[]} все твины для указанного объекта
     */
    static findByTarget(...targets) {
        return this.filter('obj', ...targets);
    }

    /** @ignore */
    static get debugInfo(){
        return `TWEENS: ${TWEENS.size} +${NEW_TWEENS.size}`;
    }

}

Ticker.on("tick", (e) => {
    Tween.tick(e.delta);
});

export default Tween;